import { z } from "zod";
import { idSchema, instantSchema, reasonSchema } from "@amber/contracts";
import { AFFILIATION_KINDS, languageCodeSchema } from "./records.js";
import { editionReadSchema, profileDetailBaseSchema } from "./onboarding.js";

export const IDENTITY_ACTIONS = {
  edit: "profiles.edit_identity",
  refresh: "profiles.refresh",
  merge: "profiles.merge",
  reverseMerge: "profiles.reverse_merge",
  split: "profiles.split",
  retire: "profiles.retire",
  reactivate: "profiles.reactivate",
  deleteDraft: "profiles.delete_draft",
  restore: "profiles.restore",
  importRoster: "profiles.import",
  exportRoster: "profiles.export",
} as const;

export const EDITOR_IDENTITY_REASON = "Only Editors and above can change profiles";
export const ADMIN_RESTORE_PROFILE_REASON = "Only Admins can restore deleted profiles";

export const partialDateSchema = z
  .string()
  .regex(/^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/, "expected a year, year-month or full date");
export type PartialDate = z.infer<typeof partialDateSchema>;

export function datePrecision(value: string): "year" | "month" | "day" {
  if (value.length === 4) return "year";
  if (value.length === 7) return "month";
  return "day";
}

export const ORGANISATION_KINDS = [
  "party",
  "government",
  "parliament",
  "office",
  "broadcaster",
  "publisher",
  "employer",
  "other",
] as const;
export const organisationKindSchema = z.enum(ORGANISATION_KINDS);
export const ORGANISATION_KIND_LABELS: Record<z.infer<typeof organisationKindSchema>, string> = {
  party: "Party",
  government: "Government",
  parliament: "Parliament",
  office: "Office",
  broadcaster: "Broadcaster",
  publisher: "Publisher",
  employer: "Employer",
  other: "Other organisation",
};

export const roleKindSchema = z.enum(AFFILIATION_KINDS);
export const ROLE_KIND_LABELS: Record<z.infer<typeof roleKindSchema>, string> = {
  membership: "Membership",
  leadership: "Leadership",
  office: "Public office",
  representation: "Representation",
  public_role: "Public role",
};

export const roleEndSchema = z.discriminatedUnion("state", [
  z.strictObject({ state: z.literal("ongoing") }),
  z.strictObject({ state: z.literal("unknown") }),
  z.strictObject({ state: z.literal("ended"), date: partialDateSchema }),
]);
export type RoleEnd = z.infer<typeof roleEndSchema>;

export const organisationRefSchema = z.strictObject({
  id: idSchema("org"),
  name: z.string(),
  kind: organisationKindSchema,
});
export type OrganisationRef = z.infer<typeof organisationRefSchema>;

export const roleSchema = z.strictObject({
  id: idSchema("aff"),
  title: z.string().min(1),
  kind: roleKindSchema,
  organisation: organisationRefSchema.nullable(),
  start: partialDateSchema.nullable(),
  end: roleEndSchema,
  evidence: z.string().min(1),
  evidence_url: z.url().nullable(),
  derivation: z.enum(["sourced", "editor"]),
  checked_at: instantSchema.nullable(),
});
export type Role = z.infer<typeof roleSchema>;

export const NAME_TYPES = ["alias", "previous_name", "public_name", "original_script", "transliteration"] as const;
export const nameTypeSchema = z.enum(NAME_TYPES);
export const NAME_TYPE_LABELS: Record<z.infer<typeof nameTypeSchema>, string> = {
  alias: "Alias",
  previous_name: "Previous name",
  public_name: "Public name",
  original_script: "Original script",
  transliteration: "Transliteration",
};

export const nameSchema = z.strictObject({
  value: z.string().min(1),
  type: nameTypeSchema,
  script: z.string(),
  language: languageCodeSchema.nullable(),
  valid_from: partialDateSchema.nullable(),
  valid_to: partialDateSchema.nullable(),
  evidence: z.string().min(1),
  derivation: z.enum(["sourced", "editor"]),
});
export type ProfileName = z.infer<typeof nameSchema>;

const evidenceText = z.string().trim().min(1, "Every new or changed value needs evidence").max(1000);

export const roleInputSchema = z.strictObject({
  id: idSchema("aff").optional(),
  title: z.string().trim().min(1, "A role needs a title").max(200),
  kind: roleKindSchema,
  organisation: z.strictObject({ name: z.string().trim().min(1).max(200), kind: organisationKindSchema }).nullable(),
  start: partialDateSchema.nullable(),
  end: roleEndSchema,
  evidence: evidenceText,
});
export type RoleInput = z.infer<typeof roleInputSchema>;

export const nameInputSchema = z.strictObject({
  value: z.string().trim().min(1).max(200),
  type: nameTypeSchema,
  language: languageCodeSchema.nullable().default(null),
  valid_from: partialDateSchema.nullable().default(null),
  valid_to: partialDateSchema.nullable().default(null),
  evidence: evidenceText,
});
export type NameInput = z.input<typeof nameInputSchema>;

export const editIdentityRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
  names: z.array(nameInputSchema).max(50),
  roles: z.array(roleInputSchema).max(50),
  birth_date: z.strictObject({ value: partialDateSchema.nullable(), evidence: evidenceText }).optional(),
});
export type EditIdentityRequest = z.input<typeof editIdentityRequestSchema>;

export const REFRESH_KINDS = ["changed", "added", "removed", "conflict"] as const;
export const refreshDifferenceSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  kind: z.enum(REFRESH_KINDS),
  now: z.string().nullable(),
  proposed: z.string().nullable(),
  evidence: z.string().nullable(),
  evidence_url: z.url().nullable(),
});
export type RefreshDifference = z.infer<typeof refreshDifferenceSchema>;

export const protectedValueSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  now: z.string().nullable(),
  source_value: z.string().nullable(),
  evidence: z.string().nullable(),
});
export type ProtectedValue = z.infer<typeof protectedValueSchema>;

export const REFRESH_SIGNAL_KINDS = ["office", "affiliation", "account", "source", "identity_conflict"] as const;
export const refreshSignalKindSchema = z.enum(REFRESH_SIGNAL_KINDS);
export type RefreshSignalKind = z.infer<typeof refreshSignalKindSchema>;
export const REFRESH_SIGNAL_LABELS: Record<RefreshSignalKind, string> = {
  office: "office change",
  affiliation: "affiliation change",
  account: "account change",
  source: "source change",
  identity_conflict: "identity conflict",
};
export const refreshSignalSchema = z.strictObject({
  kind: refreshSignalKindSchema,
  detail: z.string().min(1),
  at: instantSchema,
});
export type RefreshSignal = z.infer<typeof refreshSignalSchema>;

export const refreshProposalSchema = z.strictObject({
  id: idSchema("rfp"),
  state: z.enum(["open", "decided", "discarded"]),
  trigger: z.enum(["editor", "scheduled", "signal"]),
  signals: z.array(refreshSignalSchema),
  created_at: instantSchema,
  requested_by: z.string(),
  base_revision: z.number().int().positive(),
  editions: z.array(editionReadSchema),
  differences: z.array(refreshDifferenceSchema),
  protected: z.array(protectedValueSchema),
  unchanged: z.array(z.string()),
});
export type RefreshProposal = z.infer<typeof refreshProposalSchema>;

export const decideRefreshRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
  decisions: z.record(z.string(), z.enum(["accept", "reject"])),
});

export const lifecycleRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
});

export const reactivateRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  reason: reasonSchema,
  catch_up: z.boolean().default(false),
});

export const MERGE_FIELDS = [
  "name",
  "current_role",
  "birth_date",
  "languages",
  "portrait",
  "identity",
  "owner",
] as const;
export const mergeFieldSchema = z.enum(MERGE_FIELDS);
export type MergeField = z.infer<typeof mergeFieldSchema>;
export const MERGE_FIELD_LABELS: Record<MergeField, string> = {
  name: "Name",
  current_role: "Current role",
  birth_date: "Birth date",
  languages: "Languages",
  portrait: "Portrait",
  identity: "Wikipedia and Wikidata",
  owner: "Owner",
};
export const mergeFieldChoicesSchema = z.partialRecord(mergeFieldSchema, z.enum(["survivor", "other"]));
export type MergeFieldChoices = z.infer<typeof mergeFieldChoicesSchema>;

export const mergeFieldValueSchema = z.strictObject({
  field: mergeFieldSchema,
  label: z.string(),
  survivor: z.string().nullable(),
  other: z.string().nullable(),
});
export type MergeFieldValue = z.infer<typeof mergeFieldValueSchema>;

export const mergePreviewQuerySchema = z.object({ survivor: idSchema("prf"), other: idSchema("prf") });
export const mergePreviewSchema = z.strictObject({
  survivor: z.strictObject({
    id: idSchema("prf"),
    name: z.string(),
    state_label: z.string(),
    revision: z.number().int().positive(),
  }),
  other: z.strictObject({
    id: idSchema("prf"),
    name: z.string(),
    state_label: z.string(),
    revision: z.number().int().positive(),
  }),
  aliases_added: z.array(z.string()),
  items: z.number().int().nonnegative(),
  communications: z.number().int().nonnegative(),
  sources: z.number().int().nonnegative(),
  suggestions: z.number().int().nonnegative(),
  blocked: z.string().nullable(),
  fields: z.array(mergeFieldValueSchema),
});
export type MergePreview = z.infer<typeof mergePreviewSchema>;

export const mergeRequestSchema = z.strictObject({
  survivor_id: idSchema("prf"),
  other_id: idSchema("prf"),
  survivor_revision: z.number().int().positive(),
  other_revision: z.number().int().positive(),
  field_choices: mergeFieldChoicesSchema.default({}),
  reason: reasonSchema,
});

export const splitRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  name: z.string().trim().min(1).max(200),
  names: z.array(z.string()).max(50).default([]),
  source_ids: z.array(idSchema("src")).max(50).default([]),
  item_ids: z.array(idSchema("itm")).max(500).default([]),
  reason: reasonSchema,
});

export const reverseMergeRequestSchema = z.strictObject({ reason: reasonSchema });

export const mergeViewSchema = z.strictObject({
  id: idSchema("mrg"),
  kind: z.enum(["merge", "split"]),
  role: z.enum(["survivor", "other"]),
  other: z.strictObject({ id: idSchema("prf"), name: z.string() }),
  state: z.enum(["started", "completed", "reversing", "reversed"]),
  started_at: instantSchema,
  by: z.string(),
  reason: z.string(),
  reversible: z.boolean(),
});
export type MergeView = z.infer<typeof mergeViewSchema>;

export const referenceCountsSchema = z.strictObject({
  items: z.number().int().nonnegative(),
  attributions: z.number().int().nonnegative(),
  sources: z.number().int().nonnegative(),
  accounts: z.number().int().nonnegative(),
  plan: z.boolean(),
});
export type ReferenceCounts = z.infer<typeof referenceCountsSchema>;

export const ACCOUNT_IMPORT_STATES = ["collected", "not_collected", "unsupported", "invalid"] as const;
export const IMPORT_FLAGS = [
  "duplicate_existing",
  "duplicate_row",
  "ambiguous",
  "uncollected_account",
  "unsupported_account",
  "invalid",
] as const;
export const IMPORT_FLAG_LABELS: Record<(typeof IMPORT_FLAGS)[number], string> = {
  duplicate_existing: "Duplicate of an existing profile",
  duplicate_row: "Duplicate of another row",
  ambiguous: "Ambiguous identity",
  uncollected_account: "Account on a platform we don't collect",
  unsupported_account: "Unsupported account",
  invalid: "Invalid row",
};

export const importRowSchema = z.strictObject({
  line: z.number().int().positive(),
  name: z.string(),
  wikipedia_url: z.string().nullable(),
  wikidata_id: z.string().nullable(),
  accounts: z.array(
    z.strictObject({ url: z.string(), platform: z.string().nullable(), state: z.enum(ACCOUNT_IMPORT_STATES) }),
  ),
  flags: z.array(z.strictObject({ kind: z.enum(IMPORT_FLAGS), detail: z.string() })),
  selectable: z.boolean(),
});
export type ImportRow = z.infer<typeof importRowSchema>;

export const importPreviewRequestSchema = z.strictObject({ text: z.string().max(200_000) });
export const importPreviewSchema = z.strictObject({
  rows: z.array(importRowSchema),
  selectable: z.number().int().nonnegative(),
});
export type ImportPreview = z.infer<typeof importPreviewSchema>;
export const importRequestSchema = z.strictObject({
  text: z.string().max(200_000),
  lines: z.array(z.number().int().positive()).min(1, "Select at least one row").max(500),
  languages: z.array(languageCodeSchema).min(1).default(["en"]),
  reason: reasonSchema,
});
export const importResultSchema = z.strictObject({
  created: z.array(idSchema("prf")),
  skipped: z.array(z.strictObject({ line: z.number().int(), reason: z.string() })),
});

export const exportRequestSchema = z.strictObject({
  scope: z.enum(["shown", "all"]),
  ids: z.array(idSchema("prf")).max(5000).default([]),
});
export const exportResultSchema = z.strictObject({
  filename: z.string(),
  csv: z.string(),
  rows: z.number().int().nonnegative(),
});

export const officeHolderRequestSchema = z.strictObject({
  base_revision: z.number().int().positive(),
  profile_id: idSchema("prf"),
  from: z.iso.date(),
  evidence: evidenceText,
  reason: reasonSchema,
});

export const profileDetailSchema = profileDetailBaseSchema.extend({
  names: z.array(nameSchema),
  roles: z.array(roleSchema),
  retired: z.strictObject({ at: instantSchema, by: z.string(), reason: z.string() }).nullable(),
  merges: z.array(mergeViewSchema),
  refresh: refreshProposalSchema.nullable(),
  merged_from: z.strictObject({ id: idSchema("prf"), name: z.string() }).nullable(),
});
export type ProfileDetail = z.infer<typeof profileDetailSchema>;
